import { useState, useEffect, type FormEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchApi } from "@/services/apiService";

export default function PublicLeadCaptureFormPage() {
  const { formId } = useParams<{ formId: string }>();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverMessage, setServerMessage] = useState<string>("");

  useEffect(() => {
    if (!formId) return;
    setIsLoading(true);
    fetchApi<any>(`/lead-capture/forms/${formId}`, undefined, null)
      .then((res) => {
        const data = res?.data || res;
        if (data) {
          const fields = Array.isArray(data.fields)
            ? data.fields
            : typeof data.fields === "string"
            ? JSON.parse(data.fields || "[]")
            : [];
          setForm({
            ...data,
            fields,
          });

          // Pre-populate hidden UTM fields if present in URL
          const initial: Record<string, any> = {};
          fields.forEach((f: any) => {
            if (f.type === "utm_source") {
              initial[f.label || "utm_source"] = searchParams.get("utm_source") || "";
            } else if (f.type === "utm_campaign") {
              initial[f.label || "utm_campaign"] = searchParams.get("utm_campaign") || "";
            } else if (f.type === "utm_medium") {
              initial[f.label || "utm_medium"] = searchParams.get("utm_medium") || "";
            } else {
              initial[f.label] = "";
            }
          });
          setFormData(initial);
        }
      })
      .catch((err) => {
        console.error("Failed to load lead form:", err);
      })
      .finally(() => setIsLoading(false));
  }, [formId, searchParams]);

  const handleInputChange = (fieldLabel: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldLabel]: value }));
    if (errors[fieldLabel]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[fieldLabel];
        return next;
      });
    }
  };

  const handleCheckboxMultiChange = (fieldLabel: string, opt: string, checked: boolean) => {
    const current = Array.isArray(formData[fieldLabel]) ? formData[fieldLabel] : [];
    const next = checked ? [...current, opt] : current.filter((x: string) => x !== opt);
    handleInputChange(fieldLabel, next);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;

    const newErrors: Record<string, string> = {};
    const fields = form.fields || [];

    for (const f of fields) {
      const val = formData[f.label];
      if (f.required) {
        if (val === undefined || val === null || String(val).trim() === "" || (Array.isArray(val) && val.length === 0)) {
          newErrors[f.label] = `${f.label} is required`;
        }
      }
      if (val && f.type === "phone") {
        const digits = String(val).replace(/\D/g, "");
        if (digits.length < 10) {
          newErrors[f.label] = "Phone number must be at least 10 digits";
        }
      }
      if (val && f.type === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(String(val))) {
          newErrors[f.label] = "Please enter a valid email address";
        }
      }
    }

    if (form.privacy_policy_checkbox !== false && !privacyAgreed) {
      newErrors["privacy"] = "Please agree to the privacy policy to submit";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const res = await fetchApi<any>("/lead-capture/submit", {
        method: "POST",
        body: JSON.stringify({
          form_id: form.id,
          data: formData,
        }),
      });

      setIsSubmitted(true);
      setServerMessage(res?.message || form.success_message || "Thank you! We'll contact you shortly.");

      if (res?.redirect_url) {
        setTimeout(() => {
          window.location.href = res.redirect_url;
        }, 2500);
      }
    } catch (err: any) {
      setErrors({ form: err.message || "Failed to submit form. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setServerMessage("");
    setErrors({});
    const initial: Record<string, any> = {};
    (form?.fields || []).forEach((f: any) => {
      initial[f.label] = "";
    });
    setFormData(initial);
    setPrivacyAgreed(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <i className="ri-loader-4-line text-3xl animate-spin text-indigo-600 mb-3 block" />
          <p className="text-sm font-medium text-gray-700">Loading inquiry form...</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <i className="ri-error-warning-line text-4xl text-amber-500 mb-3 block" />
          <h2 className="text-lg font-bold text-gray-900 mb-1">Form Not Found</h2>
          <p className="text-xs text-gray-500 mb-4">The form you are trying to access does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50/50 via-slate-50 to-purple-50/40 py-10 px-4 flex flex-col justify-center items-center font-sans">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-lg border border-gray-200/80 overflow-hidden transition-all">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-7 text-white text-center sm:text-left">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-white text-[11px] font-medium tracking-wide uppercase mb-2 backdrop-blur-xs">
            <i className="ri-shield-check-line" /> Verified Inquiry Form
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-white mb-1">
            {form.title || "Inquiry Form"}
          </h1>
          {form.description && (
            <p className="text-sm text-indigo-100/90 leading-relaxed">
              {form.description}
            </p>
          )}
        </div>

        {/* Form Body or Success View */}
        <div className="p-6 sm:p-8">
          {isSubmitted ? (
            <div className="text-center py-8 px-4 animate-fadeIn">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 ring-8 ring-green-50 shadow-inner">
                <i className="ri-checkbox-circle-fill text-3xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Submission Received!</h2>
              <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed mb-6">
                {serverMessage || form.success_message || "Thank you! We'll contact you shortly."}
              </p>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" /> Submit Another Inquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {errors["form"] && (
                <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600 flex items-center gap-2">
                  <i className="ri-error-warning-fill text-sm text-red-500" />
                  {errors["form"]}
                </div>
              )}

              {/* Fields */}
              <div className="space-y-4">
                {(form.fields || []).map((field: any, idx: number) => {
                  const fieldKey = field.label || `field_${idx}`;
                  const isHidden = field.type.startsWith("utm_");
                  if (isHidden) return null;

                  return (
                    <div key={field.id || idx} className="space-y-1.5">
                      <label className="block text-xs font-semibold text-gray-700">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-0.5">*</span>}
                      </label>

                      {/* Field Inputs */}
                      {field.type === "textarea" ? (
                        <textarea
                          rows={3}
                          value={formData[fieldKey] || ""}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={field.placeholder || "Enter details..."}
                          className={`w-full text-sm border rounded-xl px-3.5 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-indigo-100 ${
                            errors[fieldKey]
                              ? "border-red-400 bg-red-50/20 focus:border-red-500"
                              : "border-gray-200 bg-white focus:border-indigo-600 text-gray-900"
                          }`}
                        />
                      ) : field.type === "dropdown" || field.type === "property_type" || field.type === "budget_range" ? (
                        <div className="relative">
                          <select
                            value={formData[fieldKey] || ""}
                            onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                            className={`w-full text-sm border rounded-xl px-3.5 py-2.5 outline-none appearance-none transition-colors focus:ring-2 focus:ring-indigo-100 cursor-pointer ${
                              errors[fieldKey]
                                ? "border-red-400 bg-red-50/20 focus:border-red-500"
                                : "border-gray-200 bg-white focus:border-indigo-600 text-gray-900"
                            }`}
                          >
                            <option value="">{field.placeholder || "Select an option"}</option>
                            {(field.options || []).map((opt: string, optIdx: number) => (
                              <option key={optIdx} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                          <i className="ri-arrow-down-s-line absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        </div>
                      ) : field.type === "radio" ? (
                        <div className="flex flex-wrap gap-3 pt-1">
                          {(field.options || []).map((opt: string, optIdx: number) => (
                            <label
                              key={optIdx}
                              className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <input
                                type="radio"
                                name={fieldKey}
                                value={opt}
                                checked={formData[fieldKey] === opt}
                                onChange={() => handleInputChange(fieldKey, opt)}
                                className="text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5 cursor-pointer"
                              />
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      ) : field.type === "checkbox" ? (
                        <div className="flex flex-wrap gap-3 pt-1">
                          {(field.options || []).map((opt: string, optIdx: number) => {
                            const isChecked = Array.isArray(formData[fieldKey]) && formData[fieldKey].includes(opt);
                            return (
                              <label
                                key={optIdx}
                                className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  value={opt}
                                  checked={isChecked}
                                  onChange={(e) => handleCheckboxMultiChange(fieldKey, opt, e.target.checked)}
                                  className="text-indigo-600 rounded focus:ring-indigo-500 h-3.5 w-3.5 cursor-pointer"
                                />
                                <span>{opt}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : field.type === "date" || field.type === "site_visit_date" ? (
                        <input
                          type="date"
                          value={formData[fieldKey] || ""}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          className={`w-full text-sm border rounded-xl px-3.5 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-indigo-100 ${
                            errors[fieldKey]
                              ? "border-red-400 bg-red-50/20 focus:border-red-500"
                              : "border-gray-200 bg-white focus:border-indigo-600 text-gray-900"
                          }`}
                        />
                      ) : field.type === "number" ? (
                        <input
                          type="number"
                          value={formData[fieldKey] || ""}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={field.placeholder || "Enter number..."}
                          className={`w-full text-sm border rounded-xl px-3.5 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-indigo-100 ${
                            errors[fieldKey]
                              ? "border-red-400 bg-red-50/20 focus:border-red-500"
                              : "border-gray-200 bg-white focus:border-indigo-600 text-gray-900"
                          }`}
                        />
                      ) : field.type === "phone" ? (
                        <input
                          type="tel"
                          value={formData[fieldKey] || ""}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={field.placeholder || "Enter phone number..."}
                          className={`w-full text-sm border rounded-xl px-3.5 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-indigo-100 ${
                            errors[fieldKey]
                              ? "border-red-400 bg-red-50/20 focus:border-red-500"
                              : "border-gray-200 bg-white focus:border-indigo-600 text-gray-900"
                          }`}
                        />
                      ) : (
                        <input
                          type={field.type === "email" ? "email" : "text"}
                          value={formData[fieldKey] || ""}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={field.placeholder || "Enter here..."}
                          className={`w-full text-sm border rounded-xl px-3.5 py-2.5 outline-none transition-colors focus:ring-2 focus:ring-indigo-100 ${
                            errors[fieldKey]
                              ? "border-red-400 bg-red-50/20 focus:border-red-500"
                              : "border-gray-200 bg-white focus:border-indigo-600 text-gray-900"
                          }`}
                        />
                      )}

                      {field.helpText && (
                        <p className="text-[11px] text-gray-400 mt-1">{field.helpText}</p>
                      )}
                      {errors[fieldKey] && (
                        <p className="text-xs text-red-500 mt-1 font-medium">{errors[fieldKey]}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Privacy Checkbox */}
              {form.privacy_policy_checkbox !== false && (
                <div className="pt-2">
                  <label className="flex items-start gap-2.5 cursor-pointer text-xs text-gray-600 leading-normal">
                    <input
                      type="checkbox"
                      checked={privacyAgreed}
                      onChange={(e) => {
                        setPrivacyAgreed(e.target.checked);
                        if (errors["privacy"]) {
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next["privacy"];
                            return next;
                          });
                        }
                      }}
                      className="mt-0.5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 h-4 w-4 cursor-pointer"
                    />
                    <span>
                      By submitting you agree to our{" "}
                      <span className="text-indigo-600 font-medium underline underline-offset-2">
                        Privacy Policy
                      </span>{" "}
                      and consent to be contacted.
                    </span>
                  </label>
                  {errors["privacy"] && (
                    <p className="text-xs text-red-500 mt-1 font-medium">{errors["privacy"]}</p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-60 text-white font-semibold text-sm rounded-xl px-6 py-3.5 shadow-md shadow-indigo-200 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>{form.submit_button_text || "Submit Inquiry"}</span>
                      <i className="ri-arrow-right-line text-sm" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer info */}
        <div className="px-6 py-3 bg-gray-50/80 border-t border-gray-100 flex items-center justify-center text-[11px] text-gray-400 gap-1">
          <i className="ri-lock-line" /> Safe & Secure • Powered by CRM Pro
        </div>
      </div>
    </div>
  );
}
